import React, {FC, useMemo, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {FileRefDto} from '../core/_models'
import {downloadAttachmentBlob, previewAttachmentBlob} from '../core/attachmentUtils'

interface RequisitionAttachmentViewerProps {
  attachment: FileRefDto
  onClose: () => void
  onDownload: (attachment: FileRefDto) => void
}

const RequisitionAttachmentViewer: FC<RequisitionAttachmentViewerProps> = ({
  attachment,
  onClose,
  onDownload,
}) => {
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const extension = useMemo(
    () => String(attachment?.fileName || '').split('.').pop()?.toLowerCase() || '',
    [attachment?.fileName]
  )
  const canInlinePreview = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)

  const formatFileSize = (bytes?: number | null): string => {
    const size = Number(bytes || 0)
    if (!size) return '-'
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
  }

  const handlePreview = async () => {
    try {
      setIsPreviewing(true)
      await previewAttachmentBlob(attachment)
    } finally {
      setIsPreviewing(false)
    }
  }

  const handleDownload = async () => {
    try {
      setIsDownloading(true)
      await downloadAttachmentBlob(attachment, attachment.fileName)
      onDownload(attachment)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1055}}
    >
      <div className='modal-dialog modal-dialog-centered' style={{maxWidth: '520px'}}>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Attachment</h5>
            <button className='btn-close' onClick={onClose}></button>
          </div>

          <div className='modal-body'>
            <div className='d-flex align-items-center gap-3 mb-3'>
              <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-2x text-primary' />
              <div>
                <div className='fw-bold text-dark'>{attachment.fileName || 'File'}</div>
                <div className='text-muted fs-7'>
                  {extension ? extension.toUpperCase() : 'FILE'} • {formatFileSize(attachment.sizeBytes)}
                </div>
              </div>
            </div>
            <div className='text-muted fs-7'>
              {canInlinePreview
                ? 'Use Preview to open this file in a new tab.'
                : 'Preview may not be supported for this file type; use Download.'}
            </div>
          </div>

          <div className='modal-footer'>
            <button className='btn btn-light' onClick={onClose}>
              Close
            </button>
            <button className='btn btn-secondary' onClick={() => void handlePreview()} disabled={isPreviewing}>
              {isPreviewing ? 'Opening...' : 'Preview'}
            </button>
            <button className='btn btn-primary' onClick={() => void handleDownload()} disabled={isDownloading}>
              {isDownloading ? 'Downloading...' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export {RequisitionAttachmentViewer}
